// src/pages/Inscription/InfoModal.jsx

import React, { useEffect } from "react";
import {
  FaAddressCard,
  FaBirthdayCake,
  FaEnvelope,
  FaIdBadge,
  FaMapMarkerAlt,
  FaPhoneAlt,
  FaTimes,
  FaUser,
  FaUsers,
} from "react-icons/fa";

import Button from "../../components/Button/Button";

const formatDate = (dateValue) => {
  if (!dateValue) return "";

  /*
   * On évite ici le décalage potentiel de date lié aux fuseaux horaires
   * lorsque l'API renvoie une date au format YYYY-MM-DD.
   */
  const normalizedDate = String(dateValue).slice(0, 10);
  const [year, month, day] = normalizedDate.split("-").map(Number);

  if (!year || !month || !day) {
    return "";
  }

  const date = new Date(year, month - 1, day);

  if (Number.isNaN(date.getTime())) {
    return "";
  }

  return date.toLocaleDateString("fr-FR", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

const normalizePhoneLink = (phone) => {
  if (!phone) return "";

  return String(phone).replace(/[^\d+]/g, "");
};

const InfoItem = ({
  icon: Icon,
  label,
  children,
  fullWidth = false,
}) => {
  if (
    children === null ||
    children === undefined ||
    children === ""
  ) {
    return null;
  }

  return (
    <div
      className={[
        "min-w-0",
        "rounded-xl",
        "border",
        "border-slate-200/80",
        "bg-slate-50/80",
        "p-3",
        "dark:border-slate-700/80",
        "dark:bg-slate-800/50",
        fullWidth ? "sm:col-span-2" : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <div className="mb-1.5 flex items-center gap-2">
        {Icon ? (
          <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-xs text-primary">
            <Icon />
          </span>
        ) : null}

        <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400 dark:text-slate-500">
          {label}
        </p>
      </div>

      <div className="break-words text-sm font-medium leading-relaxed text-slate-800 dark:text-slate-100">
        {children}
      </div>
    </div>
  );
};

const SectionTitle = ({ children }) => (
  <h3 className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-300">
    {children}
  </h3>
);

const InfoModal = ({ isOpen, onClose, acteur }) => {
  useEffect(() => {
    if (!isOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, onClose]);

  if (!isOpen || !acteur) return null;

  const {
    nom,
    prenom,
    email,
    tel,
    adresse,
    cp,
    commune,
    genre,
    date_naissance,
    club_acteur,
    licence_number,
    groupe_souhaite,
  } = acteur;

  const fullName = [nom, prenom].filter(Boolean).join(" ");
  const formattedDate = formatDate(date_naissance);
  const formattedAddress = [adresse, [cp, commune].filter(Boolean).join(" ")]
    .filter(Boolean)
    .join("\n");

  const phoneLink = normalizePhoneLink(tel);

  const handleBackdropClick = (event) => {
    if (event.target === event.currentTarget) {
      onClose();
    }
  };

  return (
    <div
      className={[
        "fixed",
        "inset-0",
        "z-50",
        "flex",
        "items-start",
        "justify-center",
        "overflow-y-auto",
        "bg-black/60",
        "p-2",
        "backdrop-blur-sm",
        "sm:items-center",
        "sm:p-4",
      ].join(" ")}
      onMouseDown={handleBackdropClick}
      role="presentation"
    >
      <div
        className={[
          "my-auto",
          "flex",
          "max-h-[calc(100dvh-1rem)]",
          "w-full",
          "max-w-lg",
          "min-h-0",
          "flex-col",
          "overflow-hidden",
          "rounded-2xl",
          "border",
          "border-slate-200/80",
          "bg-white",
          "shadow-2xl",
          "dark:border-slate-700/80",
          "dark:bg-slate-900",
          "sm:max-h-[calc(100dvh-2rem)]",
        ].join(" ")}
        role="dialog"
        aria-modal="true"
        aria-labelledby="acteur-info-modal-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="h-1.5 shrink-0 bg-gradient-to-r from-primary via-indigo-500 to-cyan-400" />

        {/* En-tête fixe */}
        <div
          className={[
            "flex",
            "shrink-0",
            "items-center",
            "justify-between",
            "gap-3",
            "border-b",
            "border-slate-200/80",
            "bg-white/95",
            "px-4",
            "py-3",
            "backdrop-blur",
            "dark:border-slate-700/80",
            "dark:bg-slate-900/95",
            "sm:px-5",
            "sm:py-4",
          ].join(" ")}
        >
          <div className="flex min-w-0 items-center gap-3">
            <div className="hidden h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary sm:flex">
              <FaAddressCard />
            </div>

            <div className="min-w-0">
              <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-primary">
                Fiche acteur
              </p>

              <h2
                id="acteur-info-modal-title"
                className="truncate text-sm font-bold text-slate-900 dark:text-white sm:text-base"
                title={fullName}
              >
                {fullName || "Informations de l’acteur"}
              </h2>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            className={[
              "inline-flex",
              "h-9",
              "w-9",
              "shrink-0",
              "items-center",
              "justify-center",
              "rounded-full",
              "text-slate-600",
              "transition-colors",
              "hover:bg-slate-200/80",
              "focus:outline-none",
              "focus:ring-2",
              "focus:ring-primary/50",
              "dark:text-slate-200",
              "dark:hover:bg-slate-700/80",
            ].join(" ")}
            title="Fermer"
            aria-label="Fermer la fenêtre"
          >
            <FaTimes />
          </button>
        </div>

        {/* Contenu défilable */}
        <div
          className={[
            "min-h-0",
            "flex-1",
            "overflow-x-hidden",
            "overflow-y-auto",
            "overscroll-contain",
            "px-4",
            "py-4",
            "sm:px-5",
            "sm:py-5",
          ].join(" ")}
        >
          <div className="space-y-5">
            {/* Identité */}
            <section className="space-y-3">
              <SectionTitle>Identité</SectionTitle>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <InfoItem
                  icon={FaUser}
                  label="Nom et prénom"
                  fullWidth={!genre}
                >
                  {fullName}
                </InfoItem>

                <InfoItem icon={FaUser} label="Genre">
                  {genre}
                </InfoItem>

                <InfoItem
                  icon={FaBirthdayCake}
                  label="Date de naissance"
                  fullWidth
                >
                  {formattedDate}
                </InfoItem>
              </div>
            </section>

            {/* Coordonnées */}
            {(email || tel || formattedAddress) && (
              <section className="space-y-3">
                <SectionTitle>Coordonnées</SectionTitle>

                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <InfoItem icon={FaEnvelope} label="Email">
                    {email ? (
                      <a
                        href={`mailto:${email}`}
                        className="break-all text-primary transition hover:underline"
                      >
                        {email}
                      </a>
                    ) : null}
                  </InfoItem>

                  <InfoItem icon={FaPhoneAlt} label="Téléphone">
                    {tel ? (
                      <a
                        href={phoneLink ? `tel:${phoneLink}` : undefined}
                        className="text-primary transition hover:underline"
                      >
                        {tel}
                      </a>
                    ) : null}
                  </InfoItem>

                  <InfoItem
                    icon={FaMapMarkerAlt}
                    label="Adresse"
                    fullWidth
                  >
                    <p className="whitespace-pre-line">
                      {formattedAddress}
                    </p>
                  </InfoItem>
                </div>
              </section>
            )}

            {/* Club et informations Judo */}
            {(club_acteur ||
              licence_number ||
              groupe_souhaite) && (
              <section className="space-y-3">
                <SectionTitle>
                  Club & informations Judo
                </SectionTitle>

                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <InfoItem
                    icon={FaIdBadge}
                    label="Club"
                    fullWidth={!licence_number}
                  >
                    {club_acteur}
                  </InfoItem>

                  <InfoItem
                    icon={FaIdBadge}
                    label="Numéro de licence"
                  >
                    {licence_number}
                  </InfoItem>

                  <InfoItem
                    icon={FaUsers}
                    label="Groupes souhaités"
                    fullWidth
                  >
                    <p className="whitespace-pre-wrap">
                      {groupe_souhaite}
                    </p>
                  </InfoItem>
                </div>
              </section>
            )}
          </div>
        </div>

        {/* Pied fixe */}
        <div
          className={[
            "shrink-0",
            "border-t",
            "border-slate-200/80",
            "bg-white/95",
            "px-4",
            "py-3",
            "backdrop-blur",
            "dark:border-slate-700/80",
            "dark:bg-slate-900/95",
            "sm:px-5",
            "sm:py-4",
          ].join(" ")}
        >
          <Button
            type="button"
            text="Fermer"
            icon={FaTimes}
            onClick={onClose}
            className={[
              "w-full",
              "justify-center",
              "gap-2",
              "border-none",
            ].join(" ")}
          />
        </div>
      </div>
    </div>
  );
};

export default InfoModal;